import React from 'react';
import classNames from 'classnames';
import classes from './classes.module.scss';
import { isKA, isPS, convertStringToCamelCase } from '../../../../../common/config/utils';
import {
  getAvailableMinSalesPrice, getAvailableSalesPrice, formatLocalCurrency
} from '../../../../config/util';
import { minSalesPriceType, liveModeState } from '../../../../config/const';
import { SvgIcon } from '../../../../../common/sprites';

const needRenderCoupon = ({ localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice }) => (
  localLiveModeMinPromoPrices
  && userAdvisorModeMinSalesPrice === minSalesPriceType.couponLiveModeMinSalesPrice
  && localLiveModeMinPromoPrices[convertStringToCamelCase(minSalesPriceType.couponLiveModeMinSalesPrice)]
);

const renderCouponImg = ({ localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice }) => (needRenderCoupon({ localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice }) ? (
  <SvgIcon id="advisor_special_offer_icon" className={ classes.discountCouponIcon } size={ 21 } />
) : null);

const priceColor = ({ availableMinSalesPrice, userAdvisorModeMinSalesPrice, localLiveModeMinPromoPrices }) => {
  if (needRenderCoupon({ localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice })) return classes.salePriceTextWithCoupon;
  if (availableMinSalesPrice) return classes.salePriceText;
  return classes.priceText;
};

const prePriceText = ({
  availableMinSalesPrice, availableMinPrice, text, currency
}) => {
  if (availableMinSalesPrice) {
    if (isPS()) {
      return (
        <>
          { text }
          <span className={ classes.crosoutPrice }>{`${ formatLocalCurrency(availableMinPrice, currency) }`}</span>
        </>
      );
    }
    return <span className={ classes.crosoutPrice }>{isKA() ? `$${ parseFloat(availableMinPrice).toFixed(2) }/min` : `${ formatLocalCurrency(availableMinPrice, currency) }`}</span>;
  }
  return <span className={ classes.minPriceText }>{ text }</span>;
};

const getPrices = ({
  liveModes, localLiveModeMinPrice, userAdvisorModeSalePrice, localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice
}) => {
  const onlineLiveModePrices = [];
  liveModes?.forEach((mode) => {
    if (mode.modeState === liveModeState.online) {
      const { localPrice, localPromoPrices } = mode;

      const parsedPrice = parseFloat(localPrice);
      const availableSalesPrice = getAvailableSalesPrice({ localPromoPrices, userAdvisorModeSalePrice, localPrice });
      const parsedAvailableSalesPrice = parseFloat(availableSalesPrice);
      onlineLiveModePrices.push({ availableMinPrice: parsedPrice, availableMinSalesPrice: parsedAvailableSalesPrice });
    }
  });

  const minPrice = Math.min(
    ...onlineLiveModePrices.flatMap(item => [item.availableMinPrice, item.availableMinSalesPrice]).filter(value => !Number.isNaN(value))
  );

  const minPriceObject = onlineLiveModePrices.find(item => item.availableMinPrice === minPrice || item.availableMinSalesPrice === minPrice);

  if (minPriceObject) return minPriceObject;

  return { availableMinPrice: localLiveModeMinPrice, availableMinSalesPrice: getAvailableMinSalesPrice({ localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice, localPrice: localLiveModeMinPrice }) };
};

function PriceText({
  localLiveModeMinPrice,
  localLiveModeMinPromoPrices = {},
  text,
  userAdvisorModeMinSalesPrice,
  userAdvisorModeSalePrice,
  liveModes = [],
  stacked = false,
  currency
}) {
  const { availableMinPrice, availableMinSalesPrice } = getPrices({
    liveModes, localLiveModeMinPrice, userAdvisorModeSalePrice, localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice
  });

  if (!availableMinPrice && !availableMinSalesPrice) return null;

  const couponImg = renderCouponImg({ localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice });
  const preText = prePriceText({
    availableMinSalesPrice, availableMinPrice, text, currency
  });
  const textColClass = isKA() ? classes.textColumnContainer : classes.textContainer;
  const rootClass = classNames(isKA() && classes.v2, stacked && classes.stackedWrapper);
  const formatPrice = (additionalText = '') => (
    <>
      <div className={ classes.priceSeparator } />
      <span className={ priceColor({ availableMinSalesPrice, userAdvisorModeMinSalesPrice, localLiveModeMinPromoPrices }) } suppressHydrationWarning>
        {`${ formatLocalCurrency(availableMinSalesPrice || availableMinPrice, currency) }${ additionalText }`}
      </span>
    </>
  );

  if (isPS() && stacked) {
    return (
      <div className={ rootClass }>
        <div className={ textColClass }>
          <div className={ classes.psStackedCouponFirstRow }>
            { couponImg }
            { preText }
          </div>
          <div className={ classNames(classes.psStackedPriceRow, priceColor({ availableMinSalesPrice, userAdvisorModeMinSalesPrice, localLiveModeMinPromoPrices })) }>
            {formatPrice()}
            <span className={ classes.perMinSuffix }>/min</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={ rootClass }>
      { couponImg }
      <div className={ textColClass }>
        { preText }
        { formatPrice('/min') }
      </div>
    </div>
  );
}

export default PriceText;
